/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { shape, string, arrayOf } from 'prop-types';
import makeStyles from '@material-ui/core/styles/makeStyles';
import QuestionAnswerIcon from '@material-ui/icons/QuestionAnswer';
import ReactMarkdown from 'react-markdown';
import Helmet from 'react-helmet';
import { useUIDSeed } from 'react-uid';

const useStyles = makeStyles((theme) => ({
    titleWrapper: {
        display: 'flex',
        alignItems: 'center',
        margin: '0 0 28px 0',
        '& svg': {
            height: '20px',
            width: '20px',
        },
    },
    title: {
        color: theme.palette.colorPrimary,
        fontFamily: theme.typography.fontFamily,
        fontSize: '17px',
        margin: '0 0 0 7px',
    },
    question: {
        fontSize: '16px',
        color: theme.palette.colorPrimary,
    },
    markdown: {
        '& p': {
            color: theme.palette.colorNeutral70,
        },
        '& a': {
            color: theme.palette.colorPrimary,
        },
    },
}));

// helper function strips markdown link - matches [,] and removes brackets - matches (,) and removes () and text inside
function mdParser(answer) {
    return answer.replace(/[\][]/gi, '').replace(/\([^()]*\)/g, '');
}

// ld+json schema
const FaqSeoSchema = ({ faqArray }) => (
    <Helmet>
        <script type="application/ld+json">
            {`{"@context": "https://schema.org","@type": "FAQPage","mainEntity": [${faqArray && faqArray.map((faqData) => (`{"@type": "Question","name": "${faqData.question}","acceptedAnswer": {"@type": "Answer","text": "${mdParser(faqData.answer)}"}}`))}]}`}
        </script>
    </Helmet>
);

FaqSeoSchema.propTypes = {
    faqArray: arrayOf(shape({
        question: string,
        answer: string,
    })).isRequired,
};

const FaqBlock = ({ faqBlock }) => {
    const classes = useStyles();
    const faqData =  faqBlock?.faq || {};
    const faqArray = faqData?.faqs || [];
    const faqHeader = faqData?.title || '';
    const seed = useUIDSeed();
    if (faqArray.length) {
        return (
            <>
                <FaqSeoSchema faqArray={faqArray} />
                {faqHeader && (
                    <div className={classes.titleWrapper}>
                        <QuestionAnswerIcon />
                        <h2 className={classes.title}>{faqHeader}</h2>
                    </div>
                )}
                {faqArray.map((data) => (
                    <div key={seed(data)}>
                        <h3 className={classes.question}>{data.question}</h3>
                        <ReactMarkdown
                            source={data.answer}
                            skipHtml
                            className={classes.markdown}
                        />
                    </div>
                ))}
            </>
        );
    }
    return null;
};

FaqBlock.propTypes = {
    faqBlock: shape({
        faq: shape({
            faqs: arrayOf(shape({
                question: string,
                answer: string,
            })).isRequired,
        }),
        title: string,
    }).isRequired,
};

export default FaqBlock;
